import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ResearchSponsorSummary } from '@/types/research'

type Props = {
  sponsors: ResearchSponsorSummary[]
  filters: { search: string; archived: string }
}

function formatMoney(value: string | null) {
  if (!value) return '—'
  const num = parseFloat(value)
  if (isNaN(num) || num === 0) return '—'
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

export default function ResearchSponsorsIndex({ sponsors, filters }: Props) {
  const [search, setSearch] = useState(filters.search)
  const [deleteTarget, setDeleteTarget] = useState<ResearchSponsorSummary | null>(null)
  const archivedView = Boolean(filters.archived)

  function applyFilters(next: { archived?: string }) {
    const merged = { search, archived: filters.archived, ...next }
    router.get(
      '/admin/research/sponsors',
      Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== '')),
      { preserveState: true },
    )
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    applyFilters({})
  }

  function confirmDelete() {
    if (!deleteTarget) return
    router.delete(`/admin/research/sponsors/${deleteTarget.id}`)
    setDeleteTarget(null)
  }

  return (
    <>
      <Head title="Research - Sponsors" />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="flex items-center justify-between gap-3">
          <form onSubmit={handleSearch} className="max-w-sm flex-1">
            <Input
              placeholder="Search sponsors..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </form>
          <div className="flex gap-1">
            <Button
              variant={!archivedView ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => applyFilters({ archived: '' })}
            >
              Active
            </Button>
            <Button
              variant={archivedView ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => applyFilters({ archived: '1' })}
            >
              Archived
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Sponsors are created automatically from event entries.
          </p>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Professional Network</TableHead>
                <TableHead>Website</TableHead>
                <TableHead className="text-center">Events Sponsored</TableHead>
                <TableHead className="text-right">Total Amount</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sponsors.map(sponsor => (
                <TableRow key={sponsor.id}>
                  <TableCell className="font-medium">
                    <Link href={`/admin/research/sponsors/${sponsor.id}`} className="hover:underline">
                      {sponsor.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{sponsor.industry || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {sponsor.website ? (
                      <a
                        href={sponsor.website}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline"
                      >
                        {sponsor.website}
                      </a>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-center">{sponsor.sponsorships_count}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMoney(sponsor.total_amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button asChild variant="ghost" size="sm">
                        <Link href={`/admin/research/sponsors/${sponsor.id}`}>View</Link>
                      </Button>
                      {archivedView ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              router.patch(`/admin/research/sponsors/${sponsor.id}/restore`)
                            }
                          >
                            Restore
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget(sponsor)}
                          >
                            Delete
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button asChild variant="ghost" size="sm">
                            <Link href={`/admin/research/sponsors/${sponsor.id}/edit`}>Edit</Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            onClick={() =>
                              router.patch(`/admin/research/sponsors/${sponsor.id}/archive`)
                            }
                          >
                            Archive
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {sponsors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    {archivedView
                      ? 'No archived sponsors.'
                      : 'No sponsors yet. Add sponsors from an event’s Sponsors section.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently delete sponsor</DialogTitle>
            <DialogDescription>
              Permanently delete &ldquo;{deleteTarget?.name}&rdquo;? This removes its
              {deleteTarget?.sponsorships_count ? ` ${deleteTarget.sponsorships_count}` : ''}{' '}
              sponsorship record(s) from events too. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

ResearchSponsorsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
